import { useCallback, useMemo } from 'react';
import { UserPlus, UserMinus } from 'lucide-react';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useFollowActions, useIsFollowing } from '@/hooks/useFollowActions';
import { useToast } from '@/hooks/useToast';

interface FollowButtonProps {
  /** The pubkey of the user to follow/unfollow. */
  pubkey: string;
  /** Optional class name overrides. */
  className?: string;
}

/**
 * Scrapbook-styled follow / unfollow button.
 *
 * When not following: polaroid-white card with tape accent + "Follow" text.
 * When following: subtle outline-style with "Following" text, hover → "Unfollow".
 *
 * Hides itself when the target is the logged-in user or when no user is logged in.
 */
export function FollowButton({ pubkey, className }: FollowButtonProps) {
  const { user } = useCurrentUser();
  const isFollowing = useIsFollowing(pubkey);
  const { isPending, follow, unfollow } = useFollowActions();
  const { toast } = useToast();

  const handleToggleFollow = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;

    try {
      if (isFollowing) {
        await unfollow(pubkey);
        toast({ title: 'Unfollowed' });
      } else {
        await follow(pubkey);
        toast({ title: 'Followed!' });
      }
    } catch (err) {
      console.error('Follow toggle failed:', err);
      toast({ title: 'Failed to update follow list', variant: 'destructive' });
    }
  }, [user, pubkey, isFollowing, follow, unfollow, toast]);

  // Don't render for own profile or when logged out
  if (!user || user.pubkey === pubkey) return null;

  return (
    <button
      type="button"
      onClick={handleToggleFollow}
      disabled={isPending}
      className={[
        'relative inline-flex items-center gap-1.5 px-4 py-2',
        'font-hand text-base font-bold transition-all active:scale-[0.95]',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        isFollowing
          ? 'bg-cream text-pol-text-secondary shadow-[0_1px_4px_rgba(45,27,78,0.06)] hover:bg-red-50 hover:text-red-500 hover:shadow-[0_2px_8px_rgba(220,38,38,0.12)] group'
          : 'bg-polaroid-white text-pol-text shadow-[0_2px_8px_rgba(45,27,78,0.1)] hover:-translate-y-0.5 hover:shadow-[0_4px_14px_rgba(45,27,78,0.14)]',
        className,
      ].filter(Boolean).join(' ')}
    >
      {/* Mini tape accent on the left edge */}
      {!isFollowing && (
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 w-[10px] h-[22px] tape-rainbow"
          style={{ transform: 'translateX(-3px) translateY(-50%)' }}
        />
      )}

      {isPending ? (
        <>
          <div className="w-3.5 h-3.5 border-2 border-pol-pink border-t-transparent rounded-full animate-spin" />
          <span>...</span>
        </>
      ) : isFollowing ? (
        <>
          <UserMinus size={14} className="hidden group-hover:block" />
          <UserPlus size={14} className="block group-hover:hidden" />
          <span className="group-hover:hidden">Following</span>
          <span className="hidden group-hover:inline">Unfollow</span>
        </>
      ) : (
        <>
          <UserPlus size={14} />
          <span>Follow</span>
        </>
      )}
    </button>
  );
}
